<template>
  <div class="add-user-container">
    <div v-if="!isEdit" class="content">
      <div class="title">{{ $t('addSecondaryUser') }}</div>

      <div class="form-container">
        <div class="input-title">{{ $t('firstName') }}</div>
        <input v-model="firstName" type="text" class="input" :placeholder="$t('createUserLastNamePlaceholder')" @change="isChanged = true" />
      </div>

      <div class="form-container">
        <div class="input-title">{{ $t('lastName') }}</div>
        <input v-model="lastName" type="text" class="input" :placeholder="$t('createUserFirstNamePlaceholder')" @change="isChanged = true" />
      </div>

      <div class="form-container">
        <div class="input-title">
          {{ $t('emailAddress') }}
        </div>
        <input ref="email" v-model="email" type="text" class="input" :placeholder="$t('createUserEmailPlaceholder')" @change="isChanged = true" />
      </div>

      <div class="form-container">
        <div class="input checkbox">
          <div class="check-box" @click="isMainUser = !isMainUser">
            <div class="checkable" :class="{ checked: isMainUser }" />
            <div class="value">{{ $t('mainUser') }}</div>
          </div>
        </div>
      </div>

      <div class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('createUserInfos') }}</div>
      </div>

      <div class="btn-container">
        <div class="main-button cancel" @click="$router.go(-1)">
          {{ $t('cancel') }}
        </div>
        <div class="main-button" @click="() => createOrEditUser(false)">
          {{ $t('addUser') }}
        </div>
      </div>
    </div>
    <div v-else class="content">
      <div class="title">{{ $t('editSecondaryUser') }}</div>

      <div class="form-container">
        <div class="input-title">{{ $t('firstName') }}</div>
        <input v-model="firstName" type="text" class="input" :placeholder="$t('createUserLastNamePlaceholder')" @change="isChanged = true" />
      </div>

      <div class="form-container">
        <div class="input-title">{{ $t('lastName') }}</div>
        <input v-model="lastName" type="text" class="input" :placeholder="$t('createUserFirstNamePlaceholder')" @change="isChanged = true" />
      </div>

      <div class="form-container">
        <div class="input checkbox">
          <div class="check-box" @click="isMainUser = !isMainUser">
            <div class="checkable" :class="{ checked: isMainUser }" />
            <div class="value">{{ $t('mainUser') }}</div>
          </div>
        </div>
      </div>

      <div class="info margin-top" :class="{ disabled: emailSent }">
        <div class="text black pointer" @click.stop="sendAccountConfirmation">
          {{ $t('sendBackAccountConfirmation') }}
        </div>
        <span v-if="emailSent">{{ $t('confirmationEmailSent') }}</span>
      </div>
      <div class="info">
        <div class="text black pointer" @click.stop="deleteUser">
          {{ $t('deleteUser') }}
        </div>
      </div>
      <div v-if="deleteErrorMessage" class="info warning">
        <div class="icon"></div>
        <div class="text">{{ deleteErrorMessage }}</div>
      </div>
      <div v-if="currentUser && currentUser.isActivated" class="info">
        <div class="text black pointer" @click.stop="disableUser">
          {{ $t('disableUser') }}
        </div>
      </div>
      <div v-else class="info">
        <div class="text black pointer" @click.stop="enableUser">
          {{ $t('enableUser') }}
        </div>
      </div>

      <div class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('userInfosDisable') }}</div>
      </div>

      <div v-if="errorMessage" class="info error">
        <div class="icon"></div>
        <div class="text">{{ errorMessage }}</div>
      </div>

      <div class="btn-container">
        <div class="main-button cancel" @click="$router.go(-1)">
          {{ $t('cancel') }}
        </div>
        <div class="main-button" @click="() => createOrEditUser(true)">
          {{ $t('change') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  beforeRouteLeave(to, from, next) {
    if (this.isSend) return next();
    if (this.isChanged && confirm(this.$t('areYouSureToExit'))) return next();
    else if (!this.isChanged) return next();
  },

  props: {
    isEdit: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      email: '',
      firstName: '',
      lastName: '',
      isMainUser: false,
      loading: false,
      error: false,
      errorMessage: null,
      users: [],
      currentUser: {},
      isChanged: false,
      isSend: false,
      emailSent: false,
      deleteErrorMessage: null,
    };
  },

  computed: {
    ...mapGetters(['user']),
  },

  async mounted() {
    if (this.$route.query.email) this.email = this.$route.query.email;
    if (this.$route.query.firstName) {
      this.firstName = this.$route.query.firstName;
    }
    if (this.$route.query.lastName) this.lastName = this.$route.query.lastName;
    if (this.$attrs.info) {
      this.users = this.$attrs.info.users;
      this.currentUser = this.$attrs.info.users.find(e => e.id === this.$route.query.userId);
    }

    if (!(this.currentUser && this.currentUser.firstName)) {
      const companyId = this.$route.query.companyId || this.user.client.id;
      this.users = await this.$store.dispatch('getUsers', { id: companyId });
      this.currentUser = await this.users.users.find(e => e.id === this.$route.query.userId);
    }
    this.isMainUser = this.currentUser ? this.currentUser.isMainUser : false;
  },

  methods: {
    async sendAccountConfirmation() {
      if (this.emailSent) return;
      await this.$store.dispatch('sendAccountConfirmation', {
        clientId: this.$route.query.companyId || this.user.client.id,
        userId: this.currentUser.id,
      });
      this.emailSent = true;
    },

    async deleteUser() {
      this.errorMessage = null;

      const result = await this.$store.dispatch('setPopup', {
        popupType: 'Confirm',
      });
      if (result) {
        try {
          await this.$store.dispatch('deleteUser', {
            clientId: this.$route.query.companyId || this.user.client.id,
            userId: this.currentUser.id,
          });
          this.$router.push({ name: 'admin' });
        } catch (error) {
          if (error.response.data.code === 400014) {
            this.deleteErrorMessage = this.$t('userMustBeDisabled');
          } else {
            this.errorMessage = this.$t('anErrorOccured');
          }
        }
      }
    },

    async enableUser() {
      const result = await this.$store.dispatch('setPopup', {
        popupType: 'enableUser',
      });
      if (result) {
        await this.$store.dispatch('editUser', {
          id: this.$route.query.userId,
          activated: true,
        });
        this.$router.go(-1);
      }
    },

    async disableUser() {
      const result = await this.$store.dispatch('setPopup', {
        popupType: 'disableUser',
        companyId: this.$route.query.companyId || this.user.client.id,
      });
      this.deleteErrorMessage = false;
      if (result.value && result.manager) {
        await this.$store.dispatch('disableUser', {
          id: this.$route.query.userId,
          userToTransferId: result.manager,
        });
        this.$router.go(-1);
      }
    },

    async createOrEditUser(isForEdit) {
      this.isSend = true;
      this.errorMessage = null;
      try {
        await this.$store.dispatch(
          isForEdit ? 'editUser' : 'addUser',
          isForEdit
            ? {
                id: this.$route.query.userId,
                firstName: this.firstName,
                lastName: this.lastName,
                isMainUser: this.isMainUser,
              }
            : {
                id: this.$route.query.companyId || this.user.client.id,
                email: this.email,
                firstName: this.firstName,
                lastName: this.lastName,
                isMainUser: this.isMainUser,
              },
        );
        this.$router.go(-1);
        this.$refs.email.setCustomValidity('');
        if (this.$route.fullPath.includes('admin')) {
          this.$router.push('/admin/account');
        } else {
          this.$router.push('/settings/users');
        }
      } catch (err) {
        if (err.response.status === 409) {
          this.$refs.email.setCustomValidity('Invalid');
          this.errorMessage = this.$t('duplicatedEmail');
        } else {
          this.errorMessage = this.$t('anErrorOccured');
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.add-user-container {
  margin: 60px 0 0;

  .content {
    max-width: 600px;

    .title {
      font-family: fonts.$title-font;
      color: colors.$darkblue-color;
      font-weight: 500;
      font-size: 22px;
      text-align: left;
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      .input-title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 16px;
        margin: 46px 0 10px 0;
      }

      .error-message {
        font-weight: 400;
        font-size: 14px;
        color: colors.$red-color;
        margin-left: 15px;
      }

      .input {
        width: 100%;
        height: 48px;
        margin: 0 0 16px;
        padding: 12px 16px;
        box-sizing: border-box;
        color: colors.$text-color;
        border: solid 1px colors.$lightgrey-color;
        border-radius: 4px;
        background: colors.$white-color;
        transition: 0.3s ease;

        &.checkbox {
          margin-top: 24px !important;

          display: flex;
          align-items: center;
          justify-content: space-around;

          border: none;
        }

        &:last-child {
          margin: 0;
        }

        &::placeholder {
          color: colors.$lightgrey-color;
        }

        &:invalid {
          border-color: colors.$red-color;
        }

        .check-box {
          cursor: pointer;
          display: flex;
          align-items: center;
          flex-grow: 1;
          transition: all 0.3s;

          .value {
            margin-left: 16px;
          }

          .checkable {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            $size: 16px;
            width: $size;
            height: $size;
            border-radius: 4px;
            border: solid 1px colors.$lightgrey-color;
            transition: 0.2s ease-out;

            &::after {
              display: block;
              position: absolute;
              content: '';
              border: solid white;
              border-width: 0 2px 2px 0;
              margin: -1px 0 0;
              padding: 2px 2px 8px 2px;
              opacity: 0;
              transform: scale(0.6);
              transition: 0.2s ease-out;
            }

            &.checked {
              border-color: colors.$main-color;
              background: colors.$main-color;

              &::after {
                opacity: 1;
                transform: rotate(45deg) scale(1);
              }
            }
          }
        }
      }

      .monitoring-dropdown,
      .dropdown-container,
      .dropdown {
        height: 40px !important;
      }
    }

    .info {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 12px 0 0 0;

      &.warning {
        .text {
          color: colors.$orange-color;
        }

        .icon {
          border-color: colors.$orange-color;

          &:after,
          &:before {
            background: colors.$orange-color;
          }
        }
      }

      &.error {
        margin-top: 42px;

        .text {
          color: colors.$red-color;
        }

        .icon {
          border-color: colors.$red-color;

          &:after,
          &:before {
            background: colors.$red-color;
          }
        }
      }

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color;
        font-size: 14px;
        opacity: 0.8;
      }
    }

    .info {
      display: flex;
      align-items: center;
      margin: 14px 0 0 0;
      font-size: 14px;

      &:hover {
        .text.black {
          color: colors.$main-color;
        }
      }

      &.disabled {
        .text.black {
          opacity: 0.3;
        }
      }

      span {
        color: colors.$lightgreen-color;
        margin-left: 20px;
        margin-bottom: 14px;
        font-weight: 600;
      }

      &.margin-top {
        margin: 64px 0 0;
      }

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color;
        font-size: 14px;
        opacity: 0.8;

        &.black {
          color: colors.$black-color;
          margin-bottom: 14px;
          font-weight: 500;
          font-size: 14px;
          line-height: 16px;
          display: flex;
          align-items: center;
          text-decoration-line: underline;
        }

        &.pointer {
          cursor: pointer;
        }
      }
    }

    .btn-container {
      display: flex;
      margin: 42px 0 24px;

      .main-button {
        margin: 0 12px 0;
        width: 200px;
        padding: 0;

        &:hover {
          cursor: pointer;
        }

        &.cancel {
          margin: 0 12px 0 0;
          background: colors.$lightgrey-color;
          color: colors.$darkgrey-color;
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .btn-container {
    flex-direction: column;
    align-items: center;

    margin-top: 36px !important;

    .main-button {
      margin: 6px 0 !important;
    }
  }
}
</style>
